<template>
    <div class="flex flex-wrap items-end gap-4 mb-5">
        <div class="flex flex-col">
            <label for="orders_date_from" class="text-sm font-medium text-gray-700">From</label>
            <input id="orders_date_from" type="date" v-model="dateFrom" class="mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
        </div>

        <div class="flex flex-col">
            <label for="orders_date_to" class="text-sm font-medium text-gray-700">To</label>
            <input id="orders_date_to" type="date" v-model="dateTo" class="mt-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
        </div>

        <button v-if="dateFrom || dateTo" type="button" @click="clearDateFilter" class="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            Clear
        </button>
    </div>

    <div class="flex items-center space-x-2 mb-5" v-if="selectedLeads.length">
        <div class="relative inline-block text-left">
            <div>
                <button @click="showDropdown = !showDropdown" type="button" class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100"  aria-expanded="true" aria-haspopup="true">
                    Mark Orders
                    <svg class="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                    </svg>
                </button>
            </div>

            <div v-if="showDropdown" class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none" role="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabindex="-1">
                <div class="py-1" role="none">
                    <span @click="markOrder('completed')" class="text-green-500 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Completed</span>
                    <span @click="markOrder('cancelled')" class="text-red-500 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer border-b border-gray-300" role="menuitem" tabindex="-1">Cancelled</span>
                    <span @click="markOrder('processing')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Processing</span>
                    <span @click="markOrder('created')" class="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer" role="menuitem" tabindex="-1">Created</span>
                </div>
            </div>
        </div>
    </div>

    <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
        <table class="min-w-full divide-y divide-gray-300">
            <thead class="bg-gray-50">
            <tr>
                <th scope="col" class="relative px-7 sm:w-12 sm:px-6">
                    <input type="checkbox" v-model="selectAll" :value="!selectAll" :checked="selectAll" class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600">
                </th>
                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Customer</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Email</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Door Style</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Amount</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Payment Status</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Shipping Address</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date</th>
                <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Details</th>
            </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
            <tr :class="{'bg-gray-50': isLeadSelected(order.id)}" v-for="(order, index) in orders_data">
                <td class="relative px-7 sm:w-12 sm:px-6">
                    <!-- Selected row marker, only show when row is selected. -->

                    <div v-if="isLeadSelected(order.id)" class="absolute inset-y-0 left-0 w-0.5 bg-indigo-600"></div>
                    <input type="checkbox" v-model="selectedLeads" :value="order.id" :checked="isLeadSelected(order.id)" class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600">
                </td>
                <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" :class="{'text-indigo-600': isLeadSelected(order.id)}">
                    <!-- <a :href="'/leads/'+order.lead.reference"> -->
                    {{ order.customer ? order.customer.name : 'N/A' }} 
                    <!-- </a> -->
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ order.customer ? order.customer.email : 'N/A' }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ (order.order_items && order.order_items.length) ? order.order_items[0].uppercase_doorstyle : 'N/A' }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ order.formatted_amount }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-left">
                    <div class="flex flex-col items-start gap-1">
                        <span
                            class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                            :style="getPaymentStatusClasses(order.payment_status).style"
                        >
                            <svg class="h-2 w-2" :style="getPaymentStatusClasses(order.payment_status).dotStyle" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3" /></svg>
                            {{ order.payment_status ? order.payment_status.replaceAll('_', ' ') : 'unpaid' }}
                        </span>
                        <span v-if="order.payment_status && order.payment_status.toLowerCase() === 'paid' && order.payment_method === 'Stripe' && order.stripe_payment_intent" class="text-xs text-gray-500 font-mono">
                            {{ order.stripe_payment_intent }}
                        </span>
                    </div>
                </td>
                <td class="px-3 py-4 text-sm text-gray-500">{{ order.full_address }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-left">
                    <span
                        class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                        :style="getStatusClasses(order.status).style"
                    >
                        <svg class="h-2 w-2" :style="getStatusClasses(order.status).dotStyle" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3" /></svg>
                        {{ order.status ? order.status.replaceAll('_', ' ') : '' }}
                    </span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ order.date || order.created_at }}</td>
                <td class="whitespace-nowrap px-3 py-4 text-xs text-gray-500 hover:text-blue-500">
                    <a :href="'/configurator-orders/'+order.id"><i class="fa-solid fa-eye text-lg"></i></a>
                    <!-- <ul v-if="lead.assigned_to.length">
                        <li v-for="(user, index) in lead.assigned_to">{{ user }}</li>
                    </ul>
                    <span v-else>-</span> -->
                </td></tr>
            </tbody>
        </table>
    </div>
</template>
<script>
export default {
    props: ['orders'],
    data() {
        return {
            dateFrom: '',
            dateTo: '',
            selectedLeads: [],
            selectAll: false,
            allOrders: this.orders,
            orders_data: this.orders,
            showDropdown: false,
            statusClasses: {
                completed: { style: { backgroundColor: '#dcfce7', color: '#15803d', boxShadow: 'inset 0 0 0 1px rgba(22, 163, 74, 0.2)' }, dotStyle: { fill: '#22c55e' } },
                cancelled: { style: { backgroundColor: '#fee2e2', color: '#b91c1c', boxShadow: 'inset 0 0 0 1px rgba(220, 38, 38, 0.1)' }, dotStyle: { fill: '#ef4444' } },
                processing: { style: { backgroundColor: '#dbeafe', color: '#1d4ed8', boxShadow: 'inset 0 0 0 1px rgba(29, 78, 216, 0.1)' }, dotStyle: { fill: '#3b82f6' } },
                created: { style: { backgroundColor: '#dbeafe', color: '#1d4ed8', boxShadow: 'inset 0 0 0 1px rgba(29, 78, 216, 0.1)' }, dotStyle: { fill: '#3b82f6' } },
            },
            paymentStatusClasses: {
                paid: { style: { backgroundColor: '#dcfce7', color: '#15803d', boxShadow: 'inset 0 0 0 1px rgba(22, 163, 74, 0.2)' }, dotStyle: { fill: '#22c55e' } },
                unpaid: { style: { backgroundColor: '#fef9c3', color: '#854d0e', boxShadow: 'inset 0 0 0 1px rgba(202, 138, 4, 0.2)' }, dotStyle: { fill: '#eab308' } },
            },
        }
    },
    watch: {
        dateFrom() {
            this.applyDateFilter();
        },
        dateTo() {
            this.applyDateFilter();
        },
        selectAll() {
            if (this.selectAll) {
                this.selectedLeads = this.orders_data.map(order => order.id);
            } else {
                this.selectedLeads = [];
            }
        }
    },
    computed: {
        // isLeadSelected() {
        //     console.log(this.orders_data.lead);
        //     return function(id) {
        //         return this.selectedLeads.includes(id)
        //     }
        // }
    },
    methods: {
        applyDateFilter() {
            const from = this.dateFrom ? new Date(`${this.dateFrom}T00:00:00`) : null;
            const to = this.dateTo ? new Date(`${this.dateTo}T23:59:59.999`) : null;

            if (!from && !to) {
                this.orders_data = this.allOrders;
                this.pruneSelectionsToDisplayedRows();
                return;
            }

            this.orders_data = this.allOrders.filter((row) => {
                const raw = row?.created_at;
                if (!raw) return true;

                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) return true;

                if (from && d < from) return false;
                if (to && d > to) return false;
                return true;
            });

            this.pruneSelectionsToDisplayedRows();
        },
        clearDateFilter() {
            this.dateFrom = '';
            this.dateTo = '';
            this.applyDateFilter();
        },
        pruneSelectionsToDisplayedRows() {
            const allowed = new Set(this.orders_data.map((row) => row.id));
            this.selectedLeads = this.selectedLeads.filter((id) => allowed.has(id));
            if (this.selectAll && this.selectedLeads.length !== this.orders_data.length) {
                this.selectAll = false;
            }
        },
        fetch() {
            axios.get('/leads/fetch').then(response => {
                if (response.data.leads) {
                    this.orders_data = response.data.leads;
                }
            })
        },
        submit() {
            axios.patch('/leads', {
                bulk_close: true,
                selected_leads: this.selectedLeads,
            }).then(response => {
                this.fetch();
                this.selectedLeads = [];
            }).catch(error => {
                //
            })
        },
        markOrder(status) {
            axios.patch('/configurator-orders', {
                bulk_close: true,
                status: status,
                close_order: 'true',
                selected_leads: this.selectedLeads,
            }).then(response => {
                window.location.reload();
            })
        },
        isLeadSelected(id) {
            return this.selectedLeads.includes(id)
        },
        getStatusClasses(status) {
            const normalizedStatus = status ? status.toLowerCase().trim() : '';
            return this.statusClasses[normalizedStatus] || this.statusClasses[status] || { style: { backgroundColor: '#f3f4f6', color: '#4b5563', boxShadow: 'inset 0 0 0 1px rgba(107, 114, 128, 0.1)' }, dotStyle: { fill: '#9ca3af' } };
        },
        getPaymentStatusClasses(status) {
            const normalizedStatus = status ? status.toLowerCase().trim() : 'unpaid';
            return this.paymentStatusClasses[normalizedStatus] || this.paymentStatusClasses['unpaid'];
        }
    }
}
</script>
